import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ethers } from 'ethers';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// --- POPUP COMPONENT ---
function Popup({ type, message, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`ct-popup ct-popup--${type}`}>
      <div className="ct-popup-icon">
        {type === 'success' ? '✓' : '✕'}
      </div>
      <div className="ct-popup-body">
        <div className="ct-popup-title">
          {type === 'success' ? 'Certificate Issued!' : 'Issuance Failed'}
        </div>
        <div className="ct-popup-msg">{message}</div>
      </div>
      <button className="ct-popup-close" onClick={onClose}>✕</button>
      <div className="ct-popup-bar" />
    </div>
  );
} 

// --- PARTICLE CANVAS COMPONENT ---
function ParticleCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 40 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: 0.8 + Math.random() * 1.6,
      speed: 0.3 + Math.random() * 0.7,
      opacity: 0.1 + Math.random() * 0.35,
      drift: (Math.random() - 0.5) * 0.4,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 255, 136, ${p.opacity})`;
        ctx.shadowColor = '#00ff88';
        ctx.shadowBlur = 6;
        ctx.fill();

        p.y -= p.speed;
        p.x += p.drift;

        if (p.y < -10) {
          p.y = canvas.height + 10;
          p.x = Math.random() * canvas.width;
        }
        if (p.x < 0 || p.x > canvas.width) {
          p.drift *= -1;
        }
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="ct-particles" />;
}

export default function App() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('issue');
  const [prevView, setPrevView] = useState('issue');
  const [form, setForm] = useState({ name: '', course: '' });
  const [vForm, setVForm] = useState({ id: '', file: null });
  const [res, setRes] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [popup, setPopup] = useState(null);

  const showPopup = (type, message) => setPopup({ type, message });
  const closePopup = () => setPopup(null);

  const switchView = (next) => {
    if (next === view) return;
    setPrevView(view);
    setView(next);
    setRes(null);
  };

  // --- WALLET LOGIC ---
  const connect = async () => {
    if (window.ethereum) {
      try {
        const accs = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setAccount(accs[0]);
      } catch (err) {
        console.error("Connection rejected");
      }
    } else {
      alert("MetaMask not detected. Please install the extension.");
    }
  };

  // --- ISSUANCE LOGIC ---
  const issue = async () => {
    if (!form.name || !form.course) return showPopup('error', 'Please fill in both Student Name and Course fields.');
    if (!account) return connect();

    setLoading(true);
    setStatusMsg("Generating Cryptographic Asset...");

    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('course', form.course);

      const init = await axios.post(`${API_URL}/generate-pdf`, fd, { responseType: "blob" });
      const certId = init.headers["cert-id"];
      const pdfHash = init.headers["pdf-hash"];

      setStatusMsg("Awaiting Digital Signature...");
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(ethers.getBytes(pdfHash));

      setStatusMsg("Anchoring to Polygon Ledger...");
      const relayFd = new FormData();
      relayFd.append('cert_id', certId);
      relayFd.append('pdf_hash', pdfHash);
      relayFd.append('signature', signature);
      relayFd.append('name', form.name);
      relayFd.append('course', form.course);

      await axios.post(`${API_URL}/relay`, relayFd);

      const url = window.URL.createObjectURL(new Blob([init.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `CERT_${certId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setStatusMsg("Successfully Issued.");
      showPopup('success', `Certificate for ${form.name} has been anchored to Polygon and downloaded.`);
      setTimeout(() => setStatusMsg(''), 3000);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || "Blockchain interaction failed. Please try again.";
      showPopup('error', msg);
    } finally {
      setLoading(false);
    }
  };

  // --- VERIFICATION LOGIC ---
  const verify = async () => {
    if (!vForm.id || !vForm.file) return showPopup('error', 'Please provide both a Certificate ID and a PDF file.');
    setLoading(true);
    setRes(null);

    const fd = new FormData();
    fd.append('file', vForm.file);
    fd.append('cert_id', vForm.id);

    try {
      const response = await axios.post(`${API_URL}/verify`, fd);
      setRes(response.data);
    } catch (err) {
      setRes({ status: "TAMPERED", msg: "Data mismatch or invalid record." });
    } finally {
      setLoading(false);
    }
  };

  // Determine slide direction based on tab order: issue=0, verify=1
  const tabOrder = { issue: 0, verify: 1 };
  const slideIn = tabOrder[view] > tabOrder[prevView] ? 'slideInRight' : 'slideInLeft';

  return (
    <div className="ct-container">

      {/* PARTICLE BACKGROUND */}
      <ParticleCanvas />

      {/* POPUP */}
      {popup && (
        <Popup type={popup.type} message={popup.message} onClose={closePopup} />
      )}

      {/* --- HERO --- */}
      <section className="ct-hero">
        <div className="ct-logo">Certifast <span className="ct-accent-green">Protocol</span></div>
        <h1>Verify Documents <br /> for the <span className="ct-accent-green">next web.</span></h1>
        <p>Issue and verify records with <span className="ct-accent-green">mathematical certainty.</span></p>
      </section>

      {/* --- MAIN CARD --- */}
      <main className="ct-card">
        {loading && (
          <div className="ct-loadingOverlay">
            <div className="ct-spinner"></div>
            <p style={{ color: 'var(--green-glow)', marginTop: '14px', fontSize: '11px', letterSpacing: '1px' }}>{statusMsg}</p>
          </div>
        )}

        <div className="ct-segmented">
          <button
            className={`ct-segBtn ${view === 'issue' ? 'ct-segBtn--active' : ''}`}
            onClick={() => switchView('issue')}
          >
            Issue
          </button>
          <button
            className={`ct-segBtn ${view === 'verify' ? 'ct-segBtn--active' : ''}`}
            onClick={() => switchView('verify')}
          >
            Verify
          </button>
        </div>

        <div className="ct-formContent">
          {view === 'issue' ? (
            <div className="ct-stack" key="issue" style={{ animationName: slideIn }}>
              {!account ? (
                <button onClick={connect} className="ct-button ct-primaryBtn">Connect Wallet</button>
              ) : (
                <>
                  <input
                    className="ct-input"
                    placeholder="Full Student Name"
                    autoComplete="off"
                    onChange={e => setForm({ ...form, name: e.target.value })}
                  />
                  <input
                    className="ct-input"
                    placeholder="Course Certification"
                    autoComplete="off"
                    onChange={e => setForm({ ...form, course: e.target.value })}
                  />
                  <button onClick={issue} className="ct-button ct-primaryBtn">Sign & Issue Certificate</button>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '10px' }}>
                    CONNECTED: <span className="ct-accent-green">{account.toUpperCase()}</span>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="ct-stack" key="verify" style={{ animationName: slideIn }}>
              <input
                className="ct-input"
                placeholder="Unique Certificate ID"
                onChange={e => setVForm({ ...vForm, id: e.target.value })}
              />
              <div className="ct-fileContainer">
                <label className="ct-fileLabel">Upload Document Asset</label>
                <input
                  type="file"
                  className="ct-fileInput"
                  onChange={e => setVForm({ ...vForm, file: e.target.files[0] })}
                />
              </div>
              <button onClick={verify} className="ct-button ct-primaryBtn">Run Verification Engine</button>

              {res && (
                <div className={`ct-res ${res.status === 'VERIFIED' ? 'ct-resOk' : 'ct-resFail'}`}>
                  <strong>{res.status === 'VERIFIED' ? '✓ Authenticity Confirmed' : '⚠ Integrity Warning'}</strong>
                  <div style={{ fontSize: '13px', marginTop: '8px', lineHeight: '1.4' }}>
                    {res.status === 'VERIFIED'
                      ? `Credential for ${res.name} is cryptographically valid on Polygon.`
                      : res.msg || "The file content has been modified from its original state."}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* --- INFO SECTIONS --- */}
      <section className="ct-infoSection">
        <div className="ct-infoItem">
          <div className="ct-infoText">
            <h3>Zero Trust <span className="ct-accent-green">Architecture</span></h3>
            <p>Every PDF contains a QR code that points to its own on-chain identity. Verification happens by comparing the local file's binary hash against the global ledger.</p>
          </div>
          <div className="ct-infoGraphic">🤝</div>
        </div>

        <div className="ct-infoItem">
          <div className="ct-infoText">
            <h3>Gasless <span className="ct-accent-green">Infrastructure</span></h3>
            <p>We utilize meta-transactions. Issuers sign the data locally, and our relayers handle the blockchain gas costs, ensuring a seamless Web2-like experience.</p>
          </div>
          <div className="ct-infoGraphic">⚡</div>
        </div>
      </section>

      <footer style={{ padding: '80px 20px', color: 'var(--text-muted)', fontSize: '11px', textAlign: 'center', letterSpacing: '2px' }}>
        POLYGON AMOY TESTNET — PROTOCOL v1.0.4
      </footer>
    </div>
  );
}
